import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Container, Link, CircularProgress, Chip } from '@mui/material';
import { LIGHT_PURPLE } from './constants';
import Requester from '../../utils/requester';
import { useTranslation } from 'react-i18next';

const SupportersList = () => {
    const { t } = useTranslation();
    const [supporters, setSupporters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const containerRef = useRef(null);
    const scrollRef = useRef(null);
    
    useEffect(() => {
        const fetchSupporters = async () => {
            try {
                const response = await Requester.get('stats/supporters/');
                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data) && data.length > 0) {
                        setSupporters(data);
                    }
                } else {
                    setError('Failed to fetch supporters');
                }
            } catch (err) {
                console.error('Error fetching supporters:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchSupporters();
    }, []);

    // Handle the infinite loop animation after the component mounts and whenever supporters change
    useEffect(() => {
        if (!loading && supporters.length > 0 && scrollRef.current) {
            // Clone the first group of items and append to create seamless loop
            const scrollContainer = scrollRef.current;
            const cloneItems = () => {
                if (scrollContainer.children.length > 0) {
                    const itemWidth = scrollContainer.scrollWidth;
                    // Set animation duration based on content width for consistent speed
                    const duration = Math.max(10, itemWidth / 80); // Adjusted speed - faster
                    scrollContainer.style.animation = `scroll ${duration}s linear infinite`;
                }
            };
            
            // Run after a short delay to ensure all items are rendered
            setTimeout(cloneItems, 100);
        }
    }, [loading, supporters]);

    // If there's an error or no supporters, don't render the component
    if ((!loading && supporters.length === 0) || error) {
        return null;
    }

    // Create duplicate arrays to ensure we have enough items for the scroll
    const multipliedSupporters = [...supporters, ...supporters, ...supporters];

    return (
        <Box sx={{ py: { xs: 6, sm: 8 }, px: { xs: 2, sm: 0 } }}>
            <Typography 
                variant="h3" 
                align="center" 
                gutterBottom
                sx={{
                    mb: { xs: 4, sm: 6 },
                    fontWeight: 'bold',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    background: 'linear-gradient(45deg, #B975FF, #8A2BE2)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                }}
            >
                {t("supportersList.header")}
            </Typography>
            
            {loading ? (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress sx={{ color: LIGHT_PURPLE }} />
                </Box>
            ) : (
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        width: '100%',
                        py: 3,
                        // Create a mask that fades content at edges
                        maskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent 100%)'
                    }}
                >
                    <Box
                        ref={scrollRef}
                        sx={{
                            display: 'flex',
                            gap: 3,
                            width: 'max-content',
                            animation: 'scroll 15s linear infinite',
                            '@keyframes scroll': {
                                '0%': { transform: 'translateX(0)' },
                                '100%': { transform: 'translateX(calc(-100% / 3))' }
                            },
                            '&:hover': {
                                animationPlayState: 'paused',
                            }
                        }}
                    >
                        {multipliedSupporters.map((supporter, index) => (
                            <Link 
                                key={`${supporter.channel_id}-${index}`}
                                href={`https://twitch.tv/${supporter.channel_name}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                    textDecoration: 'none',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: 120,
                                    transition: 'transform 0.3s',
                                    '&:hover': {
                                        transform: 'translateY(-5px)',
                                    }
                                }}
                            >
                                <Box sx={{ position: 'relative' }}>
                                    <Box
                                        component="img"
                                        src={supporter.profile_image_url !== undefined ? supporter.profile_image_url : 'http://127.0.0.1:3000/logo_nobg.png'} 
                                        alt={supporter.display_name || supporter.channel_name || 'Supporter'}
                                        onError={(e) => {
                                            e.target.onerror = null; 
                                            e.target.src = 'http://127.0.0.1:3000/logo_nobg.png';
                                        }}
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: '50%',
                                            border: supporter.is_live 
                                                ? '2px solid #F00' 
                                                : `2px solid ${LIGHT_PURPLE}`,
                                            mb: 1,
                                            transition: 'box-shadow 0.3s',
                                            '&:hover': {
                                                boxShadow: supporter.is_live 
                                                    ? '0 0 12px #F00' 
                                                    : `0 0 12px ${LIGHT_PURPLE}`,
                                            },
                                            animation: supporter.is_live 
                                                ? 'pulse 2s infinite' 
                                                : 'none',
                                            '@keyframes pulse': {
                                                '0%': {
                                                    boxShadow: '0 0 0 0 rgba(255, 0, 0, 0.7)',
                                                },
                                                '70%': {
                                                    boxShadow: '0 0 0 10px rgba(255, 0, 0, 0)',
                                                },
                                                '100%': {
                                                    boxShadow: '0 0 0 0 rgba(255, 0, 0, 0)',
                                                },
                                            },
                                        }}
                                    />
                                    {supporter.is_live && (
                                        <Chip
                                            label="LIVE"
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                bottom: '10px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: 'rgba(255, 0, 0, 0.9)',
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: '0.65rem',
                                                height: '20px',
                                                minWidth: '40px',
                                                zIndex: 1,
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                                animation: 'blink 2s infinite',
                                                '@keyframes blink': {
                                                    '0%': { opacity: 1 },
                                                    '50%': { opacity: 0.7 },
                                                    '100%': { opacity: 1 },
                                                },
                                            }}
                                        />
                                    )}
                                </Box>
                                <Typography
                                    variant="body2"
                                    align="center"
                                    sx={{
                                        color: supporter.is_live ? '#FFF' : 'white',
                                        fontWeight: supporter.is_live ? 'bold' : 'medium',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {supporter.display_name || supporter.channel_name}
                                </Typography>
                            </Link>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default SupportersList; 